import { useEffect, useRef, useState } from "react";
import FilePicker from "@/components/FilePicker";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  Megaphone, Plus, Trash, PaperPlaneTilt, ThumbsUp, ThumbsDown,
  ChatCircleDots, UploadSimple, DownloadSimple, X, FloppyDisk, ClockClockwise,
  FilePdf, CaretDown, CaretUp,
} from "@phosphor-icons/react";
import { ExpandAllToggle } from "./AccordionCard";
import FilterPills from "./FilterPills";
import PageLoader from "./PageLoader";

const TYPES = [
  { key: "ads", label: "Ads" },
  { key: "email", label: "Email marketing" },
  { key: "website", label: "Website" },
  { key: "social", label: "Social media" },
  { key: "support", label: "Customer service" },
  { key: "other", label: "Other" },
];

const typeLabel = (k) => TYPES.find(t => t.key === k)?.label || k;

function fmtDate(d) {
  if (!d) return "—";
  const p = new Date(d);
  if (isNaN(p.getTime())) return d;
  return p.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}
function fmtDateTime(d) {
  if (!d) return "—";
  const p = new Date(d);
  if (isNaN(p.getTime())) return d;
  return p.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

async function downloadFile(fileId, filename) {
  try {
    const res = await api.get(`/files/${fileId}/download`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url; a.download = filename || "file"; a.click();
    URL.revokeObjectURL(url);
  } catch (_e) { toast.error("Download failed"); }
}

function StatusPill({ status }) {
  if (!status) return null;
  const styles =
    status === "approved" ? { background: "#d1fae5", color: "#065f46", border: "1px solid #a7f3d0" } :
    status === "declined" ? { background: "#fee2e2", color: "#991b1b", border: "1px solid #fecaca" } :
    { background: "#fef3c7", color: "#78350f", border: "1px solid #fde68a" };
  return (
    <span className="label px-2 py-0.5" style={styles} data-testid={`mkt-status-${status}`}>
      {status}
    </span>
  );
}

function TypeBadge({ t }) {
  return (
    <span className="label px-2 py-0.5 border"
      style={{ borderColor: "var(--hair)", fontWeight: 700, color: "var(--text)" }}>
      {typeLabel(t)}
    </span>
  );
}

// ---------------- Admin create form ----------------
function NewUpdateForm({ userId, onCreated }) {
  const today = new Date();
  const iso = today.toISOString().slice(0, 10);
  const [form, setForm] = useState({
    update_type: "ads", week_of: iso, admin_summary: "", requested_change: "",
  });
  const filesRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.admin_summary.trim() && !form.requested_change.trim()) {
      toast.error("Add a weekly summary or a requested change");
      return;
    }
    setBusy(true);
    try {
      const files = Array.from(filesRef.current?.files || []);
      const { data } = await api.post(`/admin/clients/${userId}/marketing-updates`,
        { ...form, defer_notify: files.length > 0 });
      let ok = 0, fail = 0;
      for (const f of files) {
        try {
          const fd = new FormData();
          fd.append("file", f);
          await api.post(`/admin/clients/${userId}/marketing-updates/${data.update_id}/files`, fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          ok++;
        } catch (_e) { fail++; }
      }
      if (files.length) {
        await api.post(`/admin/clients/${userId}/marketing-updates/${data.update_id}/notify`).catch(() => {});
      }
      toast.success(`Update posted${files.length ? ` with ${ok} file${ok === 1 ? "" : "s"}` : ""}`);
      if (fail) toast.error(`${fail} file upload${fail === 1 ? "" : "s"} failed`);
      setForm({ update_type: "ads", week_of: iso, admin_summary: "", requested_change: "" });
      if (filesRef.current) filesRef.current.value = "";
      onCreated();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="panel p-5 md:p-6 space-y-3" data-testid="mkt-new-form">
      <div className="flex items-center gap-2">
        <Plus size={16} weight="bold" />
        <h3 className="title-xl text-xl">Post a weekly update</h3>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <p className="label mb-1">Update type</p>
          <select value={form.update_type} data-testid="mkt-type-select"
            onChange={(e) => setForm({ ...form, update_type: e.target.value })}
            className="w-full px-3 py-2">
            {TYPES.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
          </select>
        </div>
        <div>
          <p className="label mb-1">Week of</p>
          <input type="date" value={form.week_of} data-testid="mkt-week-of"
            onChange={(e) => setForm({ ...form, week_of: e.target.value })}
            className="w-full px-3 py-2" />
        </div>
      </div>
      <div>
        <p className="label mb-1">Weekly summary</p>
        <textarea value={form.admin_summary} rows={4}
          data-testid="mkt-summary"
          placeholder="What ran, what worked, key numbers…"
          onChange={(e) => setForm({ ...form, admin_summary: e.target.value })}
          className="w-full px-3 py-2" />
      </div>
      <div>
        <p className="label mb-1">Requested change (optional)</p>
        <textarea value={form.requested_change} rows={2}
          data-testid="mkt-requested"
          placeholder='e.g. "Increase weekly ad budget from $200 to $400"'
          onChange={(e) => setForm({ ...form, requested_change: e.target.value })}
          className="w-full px-3 py-2" />
        <p className="label mt-1 opacity-60">Leave blank if you don't need approval.</p>
      </div>
      <div>
        <p className="label mb-1">Attach files (optional, multiple)</p>
        <FilePicker ref={filesRef} testid="mkt-files" />
      </div>
      <button data-testid="mkt-submit" disabled={busy}
        className="btn-primary justify-center w-full h-12">
        <PaperPlaneTilt size={14} weight="bold" /> {busy ? "Posting…" : "Post update"}
      </button>
    </form>
  );
}

// ---------------- Comment thread ----------------
function CommentThread({ update, role, onChanged }) {
  const [text, setText] = useState("");
  const filesRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!text.trim() && !(filesRef.current?.files?.length)) return;
    setBusy(true);
    try {
      const { data: comment } = await api.post(`/marketing-updates/${update.update_id}/comments`,
        { text: text.trim() });
      const files = Array.from(filesRef.current?.files || []);
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        await api.post(`/marketing-updates/${update.update_id}/comments/${comment.comment_id}/files`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      setText("");
      if (filesRef.current) filesRef.current.value = "";
      onChanged();
    } catch (_e) { toast.error("Failed to post comment"); }
    finally { setBusy(false); }
  };

  return (
    <div className="mt-4 space-y-3" data-testid={`mkt-thread-${update.update_id}`}>
      {(update.comments || []).length > 0 && (
        <div className="space-y-2">
          {update.comments.map(c => (
            <div key={c.comment_id}
              className="hair p-3 text-sm"
              data-testid={`mkt-cmt-${c.comment_id}`}
              style={{ background: c.author_role === "admin" ? "var(--panel-2)" : "transparent" }}>
              <div className="flex items-baseline justify-between gap-2 mb-1">
                <p className="label">
                  {c.author_role === "admin" ? "Admin" : "Client"} · {c.author_name}
                </p>
                <p className="label opacity-60">{fmtDateTime(c.created_at)}</p>
              </div>
              {c.text && <p className="whitespace-pre-wrap">{c.text}</p>}
              {(c.files || []).length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {c.files.map(f => (
                    <button key={f.file_id} type="button"
                      onClick={() => downloadFile(f.file_id, f.filename)}
                      className="btn-ghost text-xs">
                      <DownloadSimple size={11} weight="bold" /> {f.filename}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      <form onSubmit={submit} className="space-y-2">
        <textarea value={text} rows={2}
          data-testid={`mkt-cmt-text-${update.update_id}`}
          placeholder={role === "admin" ? "Reply to client…" : "Reply to admin…"}
          onChange={(e) => setText(e.target.value)}
          className="w-full px-3 py-2 text-sm" />
        <div className="flex items-center gap-2 flex-wrap">
          <FilePicker ref={filesRef} testid={`mkt-cmt-files-${update.update_id}`} />
          <button data-testid={`mkt-cmt-submit-${update.update_id}`}
            disabled={busy}
            className="btn-ghost text-xs whitespace-nowrap">
            <ChatCircleDots size={12} weight="bold" /> {busy ? "Sending…" : "Post reply"}
          </button>
        </div>
      </form>
    </div>
  );
}

// ---------------- Requested change block ----------------
function RequestedChange({ update, role, onChanged }) {
  const [response, setResponse] = useState("");
  const [busy, setBusy] = useState(false);

  if (!(update.requested_change || "").trim()) return null;

  const decide = async (decision) => {
    setBusy(true);
    try {
      await api.post(`/marketing-updates/${update.update_id}/decision`,
        { decision, response });
      toast.success(decision === "approved" ? "Request approved" : "Request declined");
      setResponse("");
      onChanged();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="hair p-3 mt-3" data-testid={`mkt-request-${update.update_id}`}
      style={{ background: "var(--panel-2)" }}>
      <div className="flex items-baseline justify-between gap-2">
        <p className="label flex items-center gap-1">
          <ClockClockwise size={12} weight="bold" /> Requested change
        </p>
        <StatusPill status={update.request_status} />
      </div>
      <p className="text-sm mt-2 whitespace-pre-wrap">{update.requested_change}</p>

      {update.request_status && update.request_status !== "pending" && (
        <div className="mt-2 text-sm">
          {update.request_response && (
            <p className="whitespace-pre-wrap"
              style={{ color: "var(--text-dim)" }}>
              Client replied: “{update.request_response}”
            </p>
          )}
          <p className="label mt-1 opacity-60">
            Decided {fmtDateTime(update.request_decided_at)}
          </p>
        </div>
      )}

      {role === "client" && update.request_status === "pending" && (
        <div className="mt-3 space-y-2">
          <textarea value={response} rows={2}
            data-testid={`mkt-req-response-${update.update_id}`}
            placeholder="Optional note to admin"
            onChange={(e) => setResponse(e.target.value)}
            className="w-full px-3 py-2 text-sm" />
          <div className="flex gap-2">
            <button data-testid={`mkt-approve-${update.update_id}`}
              disabled={busy} onClick={() => decide("approved")}
              className="btn-primary text-xs flex-1 justify-center">
              <ThumbsUp size={12} weight="bold" /> Approve
            </button>
            <button data-testid={`mkt-decline-${update.update_id}`}
              disabled={busy} onClick={() => decide("declined")}
              className="btn-ghost text-xs flex-1 justify-center">
              <ThumbsDown size={12} weight="bold" /> Decline
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------- Single update card ----------------
function UpdateCard({ update, userId, role, onChanged, isOpen, onToggle, forceOpen }) {
  const [confirming, setConfirming] = useState(false);
  const [localOpen, setLocalOpen] = useState(isOpen);
  useEffect(() => { setLocalOpen(isOpen); }, [isOpen]);
  useEffect(() => {
    if (typeof forceOpen === "boolean") setLocalOpen(forceOpen);
  }, [forceOpen]);
  const open = localOpen;
  const del = async (e) => {
    e.stopPropagation();
    try {
      await api.delete(`/admin/clients/${userId}/marketing-updates/${update.update_id}`);
      toast.success("Deleted");
      onChanged();
    } catch (_e) { toast.error("Failed"); }
  };
  const attachmentCount = (update.files || []).length;
  const commentCount = (update.comments || []).length;
  const summaryLine = (update.admin_summary || "").split("\n")[0].trim();
  return (
    <div className="overflow-hidden hair" data-testid={`mkt-update-${update.update_id}`}
      style={{ background: "transparent" }}>
      <button type="button"
        data-testid={`mkt-toggle-${update.update_id}`}
        onClick={() => onToggle(update.update_id)}
        aria-expanded={open}
        className="w-full text-left p-4 md:p-5 flex items-start gap-3"
        style={{
          background: "transparent",
          border: "none",
          borderBottom: open ? "1px solid var(--hair)" : "1px solid transparent",
          cursor: "pointer",
        }}>
        <span className="mt-1 shrink-0" style={{ color: "var(--text)" }}>
          {open ? <CaretUp size={18} weight="fill" /> : <CaretDown size={18} weight="fill" />}
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <TypeBadge t={update.update_type} />
            <p className="label" style={{ fontWeight: 700, color: "var(--text)" }}>
              Week of {fmtDate(update.week_of)}
            </p>
            {update.request_status && <StatusPill status={update.request_status} />}
          </div>
          {!open && summaryLine && (
            <p className="text-sm mt-1 truncate" style={{ color: "var(--text-dim)" }}>
              {summaryLine}
            </p>
          )}
          {!open && (attachmentCount > 0 || commentCount > 0) && (
            <p className="label mt-1 opacity-60">
              {attachmentCount > 0 && `${attachmentCount} file${attachmentCount === 1 ? "" : "s"}`}
              {attachmentCount > 0 && commentCount > 0 && " · "}
              {commentCount > 0 && `${commentCount} comment${commentCount === 1 ? "" : "s"}`}
            </p>
          )}
        </div>
        {role === "admin" && (
          confirming ? (
            <span className="flex gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
              <button data-testid={`mkt-del-yes-${update.update_id}`}
                onClick={del} className="btn-primary text-xs">Yes, delete</button>
              <button onClick={(e) => { e.stopPropagation(); setConfirming(false); }}
                className="btn-ghost text-xs">
                <X size={12} />
              </button>
            </span>
          ) : (
            <button data-testid={`mkt-del-${update.update_id}`}
              onClick={(e) => { e.stopPropagation(); setConfirming(true); }}
              className="btn-ghost text-xs shrink-0">
              <Trash size={12} weight="bold" />
            </button>
          )
        )}
      </button>

      {open && (
        <div className="px-4 md:px-5 pb-5" data-testid={`mkt-body-${update.update_id}`}>
          {update.admin_summary && (
            <p className="whitespace-pre-wrap">{update.admin_summary}</p>
          )}

          {attachmentCount > 0 && (
            <div className="mt-3 flex flex-wrap gap-2">
              {update.files.map(f => (
                <button key={f.file_id} type="button"
                  onClick={() => downloadFile(f.file_id, f.filename)}
                  className="btn-ghost text-xs"
                  data-testid={`mkt-file-${f.file_id}`}>
                  <DownloadSimple size={11} weight="bold" /> {f.filename}
                </button>
              ))}
            </div>
          )}

          <RequestedChange update={update} role={role} onChanged={onChanged} />
          <CommentThread update={update} role={role} onChanged={onChanged} />
        </div>
      )}
    </div>
  );
}

// ---------------- PDF export ----------------
function PdfExport({ userId }) {
  const now = new Date();
  const defaultMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  const [month, setMonth] = useState(defaultMonth);
  const [busy, setBusy] = useState(false);

  const download = async () => {
    setBusy(true);
    try {
      const res = await api.get(`/admin/clients/${userId}/marketing-updates/pdf`, {
        params: { month }, responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `marketing-recap-${month}.pdf`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Recap downloaded");
    } catch (_e) { toast.error("Failed to build PDF"); }
    finally { setBusy(false); }
  };

  return (
    <div className="ml-auto flex items-center gap-2" data-testid="mkt-pdf-export">
      <input type="month" value={month} data-testid="mkt-pdf-month"
        onChange={(e) => setMonth(e.target.value)}
        className="px-2 py-1 text-xs" />
      <button data-testid="mkt-pdf-download" onClick={download} disabled={busy}
        className="btn-ghost text-xs whitespace-nowrap">
        <FilePdf size={12} weight="bold" /> {busy ? "Building…" : "Export PDF"}
      </button>
    </div>
  );
}

// ---------------- Main section ----------------
export default function MarketingSection({ userId, role = "admin" }) {
  const [updates, setUpdates] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [expandAll, setExpandAll] = useState(null); // null = respect openId, true/false = force
  const [filter, setFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all"); // all | pending | complete
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const url = role === "admin"
        ? `/admin/clients/${userId}/marketing-updates`
        : `/me/marketing-updates`;
      const { data } = await api.get(url);
      setUpdates(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [userId, role]);

  // Types with at least one update (used to render filter chips)
  const availableTypes = TYPES.filter(t => updates.some(u => u.update_type === t.key));

  // If the current filter no longer exists (e.g. last update of that type was deleted), reset
  useEffect(() => {
    if (filter !== "all" && !availableTypes.some(t => t.key === filter)) {
      setFilter("all");
    }
  }, [filter, availableTypes]);

  // Group updates by week
  const typed = filter === "all" ? updates : updates.filter(u => u.update_type === filter);
  const statusMatch = (u) => {
    if (statusFilter === "all") return true;
    // "pending": has a pending requested change; "complete": approved OR no change requested
    const s = u.request_status;
    if (statusFilter === "pending") return s === "pending";
    if (statusFilter === "complete") return s === "approved" || !s;
    return true;
  };
  const filtered = typed.filter(statusMatch);
  const statusCounts = {
    all: updates.length,
    pending: updates.filter(u => u.request_status === "pending").length,
    complete: updates.filter(u => u.request_status === "approved" || !u.request_status).length,
  };
  const byWeek = {};
  for (const u of filtered) {
    const wk = u.week_of || "unknown";
    byWeek[wk] = byWeek[wk] || [];
    byWeek[wk].push(u);
  }
  const weeks = Object.keys(byWeek).sort().reverse();

  // The first visually-rendered card auto-opens (topmost in the first week group)
  const newestId = weeks[0] ? byWeek[weeks[0]][0]?.update_id : null;

  const toggle = (id) => {
    setExpandAll(null); // stop broadcasting
    setOpenId(prev => (prev === id ? null : id));
  };

  return (
    <div className="space-y-6" data-testid="marketing-section">
      <div className="flex items-center gap-2 flex-wrap">
        <Megaphone size={18} weight="bold" />
        <h2 className="title-xl text-2xl">Marketing updates</h2>
        {role === "admin" && <PdfExport userId={userId} />}
      </div>

      {role === "admin" && <NewUpdateForm userId={userId} onCreated={load} />}

      {availableTypes.length > 0 && (
        <FilterPills
          testid="mkt-filter"
          value={filter}
          onChange={setFilter}
          options={[
            { k: "all", label: "All" },
            ...availableTypes.map(t => ({ k: t.key, label: t.label })),
          ]}
          trailing={weeks.length > 0 ? (
            <div className="ml-auto">
              <ExpandAllToggle testid="mkt-expand-all"
                allOpen={expandAll === true}
                onToggle={(next) => setExpandAll(next)} />
            </div>
          ) : null}
        />
      )}
      {updates.length > 0 && (
        <FilterPills
          testid="mkt-status-filter"
          value={statusFilter}
          onChange={setStatusFilter}
          options={[
            { k: "all", label: `All (${statusCounts.all})` },
            { k: "pending", label: `Pending (${statusCounts.pending})` },
            { k: "complete", label: `Complete (${statusCounts.complete})` },
          ]}
        />
      )}

      {loading ? (
        <PageLoader label="Loading updates…" testid="marketing-loader" />
      ) : weeks.length === 0 ? (
        <div className="panel p-10 text-center label" data-testid="mkt-empty">
          {role === "admin"
            ? "No updates yet — post the first weekly update above."
            : "No marketing updates yet. Your team will post here weekly."}
        </div>
      ) : (
        weeks.map(wk => (
          <div key={wk} className="space-y-3" data-testid={`mkt-week-${wk}`}>
            <div className="flex items-baseline gap-3">
              <p className="label">Week of</p>
              <h3 className="title-xl text-xl">{fmtDate(wk)}</h3>
              <p className="label opacity-60">{byWeek[wk].length} update{byWeek[wk].length === 1 ? "" : "s"}</p>
            </div>
            <div className="space-y-3">
              {byWeek[wk].map(u => (
                <UpdateCard key={u.update_id} update={u} userId={userId} role={role}
                  onChanged={load}
                  isOpen={openId === u.update_id}
                  forceOpen={expandAll === null ? undefined : expandAll}
                  onToggle={toggle} />
              ))}
            </div>
          </div>
        ))
      )}
    </div>
  );
}
